import React, { Fragment, useState } from "react";
import { Card, Button, Input, Image } from "antd";
import { CardHeader } from "Components/Atoms/CardHeader";
import { toast } from "react-toastify";
import AdminServices from "services/api/AdminServices";

export default function UploadToCDN() {
    const [files, setFiles] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [folderName, setFolderName] = useState("");
    const [customFileName, setCustomFileName] = useState("");
    const [uploading, setUploading] = useState(false);

    const isMultiple = files.length > 1;
    const singleFile = files[0];

    /* ---------- Helpers ---------- */

    const getFileExtension = (fileName = "") => {
        const idx = fileName.lastIndexOf(".");
        return idx !== -1 ? fileName.slice(idx + 1) : "";
    };

    const sanitizeFileName = (name = "") =>
        name
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-_]/g, "");

    /* ---------- Handlers ---------- */

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (!selectedFiles.length) return;

        setFiles(selectedFiles);
        setShowPreview(false);
        setCustomFileName("");

        if (selectedFiles.length === 1) {
            setPreviewUrl(URL.createObjectURL(selectedFiles[0]));
        } else {
            setPreviewUrl(null);
        }
    };

    const renderPreview = () => {
        if (!singleFile || !previewUrl) return null;

        const type = singleFile.type;

        if (type.startsWith("image/")) {
            return <Image src={previewUrl} style={{ maxWidth: "20rem" }} />;
        }

        if (type === "application/pdf") {
            return (
                <iframe
                    src={previewUrl}
                    title="PDF Preview"
                    style={{ width: "100%", height: "400px", border: "1px solid #ddd" }}
                />
            );
        }

        if (type.includes("json") || type.startsWith("text/")) {
            return (
                <iframe
                    src={previewUrl}
                    title="Text Preview"
                    style={{ width: "100%", height: "300px", border: "1px solid #ddd" }}
                />
            );
        }

        return (
            <div style={{ padding: "1rem", border: "1px dashed #ccc", background: "#fafafa" }}>
                <strong>{singleFile.name}</strong>
                <div>Type: {singleFile.type || "Unknown"}</div>
                <div>Size: {(singleFile.size / 1024).toFixed(2)} KB</div>
            </div>
        );
    };

    const uploadSingleFile = async (file) => {
        const ext = getFileExtension(file.name);

        // sanitize ONLY custom name
        const safeCustomName = customFileName
            ? sanitizeFileName(customFileName)
            : "";

        // ORIGINAL NAME USED AS-IS
        const finalFileName =
            !isMultiple && safeCustomName
                ? `${safeCustomName}.${ext}`
                : file.name; // <-- untouched original

        const data = new FormData();
        data.append("file", file);

        const res = await AdminServices.updateToS3({
            fileName: finalFileName,
            folderName,
            data,
        });

        return { res, finalFileName };
    };


    const handleUpload = async () => {
        if (!files.length || !folderName) {
            toast.error("Please select file(s) and folder name");
            return;
        }

        setUploading(true);

        for (const file of files) {
            try {
                const { res, finalFileName } = await uploadSingleFile(file);

                if (res?.type === true && res?.data === "success") {
                    toast.success(`Uploaded: ${finalFileName}`);
                } else {
                    toast.error(`Failed: ${file.name}`);
                }
            } catch {
                toast.error(`Error uploading: ${file.name}`);
            }
        }

        setFiles([]);
        setPreviewUrl(null);
        setCustomFileName("");
        setShowPreview(false);
        setUploading(false);
    };

    /* ---------- UI ---------- */

    return (
        <Fragment>
            <Card title={<CardHeader heading={"Upload File"} />}>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

                    {/* Folder */}
                    <Input
                        placeholder="Folder name (e.g. app-assets/docs/reports)"
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                    />

                    {/* File input */}
                    <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                    />

                    {/* Custom filename – ONLY single file */}
                    {!isMultiple && singleFile && (
                        <>
                            <Input
                                placeholder="Optional custom file name (without extension)"
                                value={customFileName}
                                onChange={(e) => setCustomFileName(e.target.value)}
                            />
                            <div style={{ fontSize: "12px", color: "#999" }}>
                                Extension: .{getFileExtension(singleFile.name)}
                            </div>
                        </>
                    )}

                    {/* Preview – ONLY single file */}
                    {!isMultiple && singleFile && (
                        <Button onClick={() => setShowPreview(v => !v)}>
                            {showPreview ? "Hide Preview" : "Preview File"}
                        </Button>
                    )}

                    {!isMultiple && showPreview && (
                        <div style={{ marginTop: "0.5rem" }}>
                            {renderPreview()}
                        </div>
                    )}

                    {/* Multiple files info */}
                    {isMultiple && (
                        <div style={{ fontSize: "13px", color: "#666" }}>
                            {files.length} files selected. Preview & rename disabled.
                        </div>
                    )}

                    {/* Upload */}
                    <Button
                        type="primary"
                        disabled={!files.length || !folderName}
                        loading={uploading}
                        onClick={handleUpload}
                    >
                        Upload to CDN
                    </Button>
                </div>
            </Card>
        </Fragment>
    );
}
